import { useEffect, useState, useMemo } from "react";
import api, { errorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Map, Building2, Handshake, User, Landmark, Radio, RefreshCw } from "lucide-react";

const TYPES = [
  { key: "branches",  label: "Branches",   Icon: Building2, color: "cyan"    },
  { key: "partners",  label: "Partners",   Icon: Handshake, color: "amber"   },
  { key: "customers", label: "Customers",  Icon: User,      color: "emerald" },
  { key: "bank_staff", label: "Bank Staff", Icon: Landmark, color: "purple" },
  { key: "live",      label: "Live Now",   Icon: Radio,     color: "orange"  },
];

const COLOR_CLS = {
  cyan:    { bg: "bg-cyan-500/10 border-cyan-500/30 text-cyan-300",       chip: "bg-cyan-500 text-slate-900" },
  amber:   { bg: "bg-amber-500/10 border-amber-500/30 text-amber-300",    chip: "bg-amber-500 text-slate-900" },
  emerald: { bg: "bg-emerald-500/10 border-emerald-500/30 text-emerald-300", chip: "bg-emerald-500 text-slate-900" },
  purple:  { bg: "bg-purple-500/10 border-purple-500/30 text-purple-300", chip: "bg-purple-500 text-slate-900" },
  orange:  { bg: "bg-orange-500/10 border-orange-500/30 text-orange-300", chip: "bg-orange-500 text-slate-900" },
};

export default function AdminMasterMap() {
  const [data, setData] = useState(null);
  const [enabled, setEnabled] = useState({ branches: true, partners: true, customers: true, bank_staff: true, live: true });
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/admin/master-map").then(r => setData(r.data)).catch(e => toast.error(errorMessage(e))).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const pins = useMemo(() => {
    if (!data) return [];
    const list = [];
    if (enabled.branches) for (const b of data.branches) if (b.branch_lat && b.branch_lng) list.push({ type: "branches", lat: b.branch_lat, lng: b.branch_lng, title: b.branch_name, sub: `${b.district_name} · ${b.branch_code||""}`, ref: b });
    if (enabled.partners) for (const p of data.partners) if (p.office_lat && p.office_lng) list.push({ type: "partners", lat: p.office_lat, lng: p.office_lng, title: p.name, sub: `${p.partner_id||"—"} · ${p.branch_name||p.district||""}`, ref: p });
    if (enabled.bank_staff) for (const s of data.bank_staff) if (s.branch_lat && s.branch_lng) list.push({ type: "bank_staff", lat: s.branch_lat, lng: s.branch_lng, title: s.name, sub: `${s.branch_name||s.district||""}`, ref: s });
    if (enabled.customers) for (const c of data.customers) if (c.location?.lat && c.location?.lng) list.push({ type: "customers", lat: c.location.lat, lng: c.location.lng, title: c.name, sub: `${c.lead_id} · ${c.district}`, ref: c });
    if (enabled.live) for (const l of data.live_shares) list.push({ type: "live", lat: l.lat, lng: l.lng, title: `LIVE · ${l.phone}`, sub: `Expires ${new Date(l.expires_at).toLocaleTimeString("en-IN",{hour:"2-digit",minute:"2-digit"})}`, ref: l });
    return list;
  }, [data, enabled]);

  const center = useMemo(() => {
    if (selected) return { lat: selected.lat, lng: selected.lng, z: 15 };
    if (pins.length === 0) return { lat: 11.1271, lng: 78.6569, z: 7 }; // Tamil Nadu center
    const avgLat = pins.reduce((a, p) => a + p.lat, 0) / pins.length;
    const avgLng = pins.reduce((a, p) => a + p.lng, 0) / pins.length;
    return { lat: avgLat, lng: avgLng, z: 8 };
  }, [pins, selected]);

  return (
    <div className="space-y-4" data-testid="admin-master-map">
      {/* Header + stats */}
      <div className="card-surface p-5">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-lg bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 flex items-center justify-center"><Map className="w-5 h-5"/></div>
          <div className="flex-1">
            <div className="font-heading text-xl text-white">Master Map</div>
            <div className="text-sm text-slate-500">Branches, partners, bank staff, customers &amp; live shares — one view.</div>
          </div>
          <button data-testid="master-map-refresh" onClick={load} className="text-slate-400 hover:text-cyan-400 p-2 rounded-full"><RefreshCw className="w-4 h-4"/></button>
        </div>
        {loading || !data ? <div className="text-slate-500 text-sm">Loading…</div> : (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            {TYPES.map(t => {
              const c = COLOR_CLS[t.color];
              const count = t.key === "live" ? data.counts.live_now :
                            t.key === "customers" ? data.counts.customers_with_location :
                            data.counts[t.key];
              return (
                <button
                  key={t.key}
                  onClick={() => setEnabled(p => ({ ...p, [t.key]: !p[t.key] }))}
                  data-testid={`toggle-${t.key}`}
                  className={`rounded-xl border p-3 flex items-center gap-2 transition-all ${c.bg} ${enabled[t.key] ? "opacity-100" : "opacity-40"} hover:opacity-90`}>
                  <t.Icon className="w-4 h-4"/>
                  <div className="flex-1 text-left">
                    <div className="text-[9px] uppercase tracking-widest font-bold">{t.label}</div>
                    <div className="text-lg font-mono-alt font-black text-white">{count || 0}</div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {/* Map */}
        <div className="md:col-span-2 card-surface p-2">
          <iframe
            title="Master Map"
            className="w-full h-[560px] rounded-lg"
            src={`https://maps.google.com/maps?q=${center.lat},${center.lng}&z=${center.z}&output=embed`}
            loading="lazy"
          />
          {selected && (
            <div className="mt-2 rounded-lg border border-white/10 bg-slate-950/60 p-3 flex items-center gap-3" data-testid="selected-pin">
              <span className={`inline-flex w-8 h-8 rounded-lg items-center justify-center ${COLOR_CLS[TYPES.find(t=>t.key===selected.type)?.color || "cyan"].chip}`}>
                {(() => { const t = TYPES.find(t=>t.key===selected.type); return t ? <t.Icon className="w-4 h-4"/> : null; })()}
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-white font-semibold">{selected.title}</div>
                <div className="text-[11px] text-slate-500">{selected.sub}</div>
              </div>
              <a target="_blank" rel="noreferrer" href={`https://maps.google.com/?q=${selected.lat},${selected.lng}`} className="text-cyan-400 text-xs hover:underline">Open</a>
              <button onClick={()=>setSelected(null)} className="text-slate-500 text-xs hover:text-white">×</button>
            </div>
          )}
        </div>

        {/* Pin list */}
        <div className="card-surface p-3 max-h-[600px] overflow-y-auto">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2 sticky top-0 bg-slate-900/95 backdrop-blur py-1">
            {pins.length} Pin{pins.length===1?"":"s"} Visible
          </div>
          <div className="space-y-1">
            {pins.map((p, i) => {
              const c = COLOR_CLS[TYPES.find(t=>t.key===p.type)?.color || "cyan"];
              return (
                <button
                  key={i}
                  data-testid={`pin-row-${p.type}-${i}`}
                  onClick={() => setSelected(p)}
                  className={`w-full text-left rounded-lg border p-2 hover:bg-white/5 flex items-center gap-2 ${selected===p ? "border-amber-500/70" : "border-white/5"}`}>
                  <span className={`w-2 h-2 rounded-full ${c.chip}`}/>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white truncate">{p.title}</div>
                    <div className="text-[10px] text-slate-500 truncate">{p.sub}</div>
                  </div>
                </button>
              );
            })}
            {pins.length === 0 && <div className="text-slate-500 text-xs italic p-3 text-center">Toggle a category above to see pins.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
